// src/UserDashboard.tsx
//
// Dashboard střelce (/user): info karty (uvolněné místo z čekací listiny,
// rozhodčí, LIVE, ukončený závod, zpráva adminů), členské příspěvky a seznam
// závodů s přihlášením. Titulek stránky dodává AppShell/Screen v App.tsx,
// nepřihlášené odchytí RequireAuth. Cena se počítá v utils/pricing.ts
// (přesun beze změny výpočtu). Obsazenost je z veřejného race_stats
// (utils/raceStats.ts – cizí přihlášky už číst nejde), čekací listiny
// z utils/waitlist.ts.
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { toDisciplineId, type DisciplineId } from "./utils/disciplines";
import { calculatePrice, toDateSafe } from "./utils/pricing";
import { useCurrentUser } from "./utils/useCurrentUser";
import { useEmailVerification } from "./utils/emailVerification";
import { registrationClosesAtOf } from "./utils/registrationDeadline";
import { isIOS } from "./utils/platform";
import { capacityOf, isRaceFull, loadRegisteredCounts } from "./utils/raceStats";
import { loadMyWaitlist } from "./utils/waitlist";
import { reportWarning } from "./utils/reportError";
import { useJudgeRaces } from "./utils/useJudgeRaces";
import { isTestRace } from "./utils/testRace";
import { useAddToCalendar } from "./AddToCalendar";
import { DisciplineBadge } from "./ui/DisciplineBadge";
import { InfoCard } from "./ui/Cards";
import { EmailVerificationNotice } from "./ui/EmailVerificationNotice";
import { SectionHeader } from "./ui/Section";
import { AsyncState, useAsync } from "./ui/AsyncState";

interface Race {
  id: string;
  name: string;
  /** Kulovnice / malorážka (starší závody pole nemají) */
  discipline?: DisciplineId;
  shootingRange: string; // nový název střelnice
  year: number;
  raceNumber: number; // číslo závodu, padované na 2 číslice
  priceMember: number;
  priceNonMember: number;
  priceWoman: number;
  priceJunior: number;
  date: any; // Firestore Timestamp nebo řetězec
  mapUrl: string;
  rulesPdfUrl?: string;
  organizedByLKS?: boolean;

  // Registrace do + viditelnost po závodě
  registrationClosesAt?: Date; // default: začátek dne závodu 00:00 v Praze
  visibleUntil?: Date; // default: závod + 3 dny do 23:59:59
  liveScoring?: boolean;
  status?: string;
  livePublic?: boolean;
  finishedAt?: Date; // kdy byl závod ukončen
  /** Kapacita (kladné číslo); bez ní neomezeno – i prázdné a 0 (capacityOf). */
  maxCapacity?: number;
}

interface User {
  uid: string;
  birthYear: number;
  gender: string;
  membership: { [year: string]: string };
  zpValidity: string;
  zpNumber: string;
  role?: string;
  email?: string;
  sequenceNumber: string;
}

interface MembershipFee {
  id: string;
  year: number;
  price: number;
  active: boolean;
}

interface Registration {
  id: string;
  raceId: string;
  startFee: number;
  paidAmount: number;
}

interface ShootingRange {
  name: string;
  lat: number;
  lng: number;
}

// ✅ Helper: start dne (00:00:00.000)
const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

// ✅ Helper: konec dne (23:59:59.999)
const endOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

// ✅ hezký formát datum+čas (cs-CZ)
const formatDateTimeCZ = (d: Date): string =>
  d.toLocaleString("cs-CZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Přihlašování je otevřené (uzávěrku má každý závod – výchozí půlnoc dne závodu). */
const isRegistrationOpen = (race: Race, now: Date): boolean =>
  !!race.registrationClosesAt && now < race.registrationClosesAt;

/** Vzhled zprávy adminů (defaults/mapWarning) podle barvy z DB. */
const WARNING_TONES: Record<
  string,
  { tone: "danger" | "warning" | "info" | "success"; icon: string }
> = {
  danger: { tone: "danger", icon: "bi-exclamation-triangle-fill" },
  warning: { tone: "warning", icon: "bi-exclamation-circle-fill" },
  info: { tone: "info", icon: "bi-info-circle-fill" },
  success: { tone: "success", icon: "bi-check-circle-fill" },
};

interface RaceCardProps {
  race: Race;
  /** Cena pro aktuálního uživatele (0, dokud není načtený – jako dřív). */
  price: number;
  /** Registrace uživatele na tento závod, pokud existuje. */
  reg: Registration | undefined;
  /** Počet přihlášených (null = neznáme / ještě nenačteno). */
  regCount: number | null;
  /** Uživatel je na čekací listině závodu. */
  onWaitlist: boolean;
  /** Předpověď počasí pro den závodu, pokud je. */
  weather?: string;
  /** Uživatel je rozhodčím na tomto (aktivním) závodě. */
  isJudge: boolean;
  onNavigate: (to: string) => void;
}

/**
 * Karta závodu: disciplína, datum, střelnice, cena pro uživatele, uzávěrka
 * (vždy, vč. výchozí půlnoci dne závodu), obsazenost a JEDEN akční stav –
 * Přihlásit / Plno – čekací listina / Plno / Uzavřeno / Zaplaceno /
 * Přihlášen – čeká na platbu.
 * Odkazy na propozice (pdfviewer), mapu (mapviewer/Apple) a počasí zůstávají.
 */
const RaceCard: React.FC<RaceCardProps> = ({
  race,
  price,
  reg,
  regCount,
  onWaitlist,
  weather,
  isJudge,
  onNavigate,
}) => {
  const raceDateObj = toDateSafe(race.date);
  const raceDate = raceDateObj
    ? raceDateObj.toLocaleDateString()
    : "Neznámé datum";

  // ✅ registrace povolená jen do registrationClosesAt
  const registrationClosesAt = race.registrationClosesAt ?? null;
  const canRegister = isRegistrationOpen(race, new Date());

  // ✅ kapacita z race_stats (neznámý počet = neplno – kapacitu pohlídá
  // transakce přihlášky)
  const isFull = isRaceFull(regCount, race.maxCapacity ?? null);

  // Registrovaný střelec se MUSÍ vždy dostat na /payment/:raceId – je tam
  // detail registrace a jediné tlačítko „Odhlásit ze závodu" (u závodu
  // zdarma by disabled „Zaplaceno" cestu úplně zavřelo). Plný závod
  // s otevřeným přihlašováním vede na čekací listinu (je jen tam).
  const isClickable = !!reg || canRegister;

  const openPayment = () => onNavigate(`/payment/${race.id}`);
  // „Přidat do kalendáře" – jen u závodu, který teprve bude
  const calendar = useAddToCalendar(race);

  // Odkazy pod kartou – každý ve vlastní list-group s mezerou (v jedné
  // list-group by Bootstrap druhé a další položce ubral horní okraj)
  const linkItemClass =
    "list-group-item list-group-item-action d-flex justify-content-between align-items-center";
  const linkItems: React.ReactNode[] = [];
  if (race.rulesPdfUrl) {
    linkItems.push(
      <button
        key="pdf"
        type="button"
        className={linkItemClass}
        onClick={(e) => {
          e.stopPropagation();
          onNavigate(`/pdfviewer/${race.id}`);
        }}
      >
        <span>
          <i className="bi bi-file-earmark-text me-2 text-secondary" />
          Propozice
        </span>
        <i className="bi bi-chevron-right text-muted" />
      </button>
    );
  }
  if (race.mapUrl) {
    linkItems.push(
      <button
        key="map"
        type="button"
        className={linkItemClass}
        onClick={(e) => {
          e.stopPropagation();
          onNavigate(`/mapviewer/${race.id}`);
        }}
      >
        <span>
          <i className="bi bi-geo-alt me-2 text-secondary" />
          Mapa (Google)
        </span>
        <i className="bi bi-chevron-right text-muted" />
      </button>
    );
    if (isIOS()) {
      linkItems.push(
        <a
          key="apple"
          className={linkItemClass}
          href={`http://maps.apple.com/?q=${encodeURIComponent(race.shootingRange)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <span>
            <i className="bi bi-geo-alt me-2 text-secondary" />
            Mapa (Apple)
          </span>
          <i className="bi bi-chevron-right text-muted" />
        </a>
      );
    }
  }
  if (calendar.available) {
    linkItems.push(
      <button
        key="calendar"
        type="button"
        className={linkItemClass}
        onClick={(e) => {
          e.stopPropagation();
          calendar.open();
        }}
      >
        <span>
          <i className="bi bi-calendar-plus me-2 text-secondary" />
          Přidat do kalendáře
        </span>
        <i className="bi bi-chevron-right text-muted" />
      </button>
    );
  }

  // Jeden akční stav – jedno tlačítko
  let action: JSX.Element;
  if (!reg) {
    if (canRegister && !isFull) {
      action = (
        <button
          type="button"
          className="btn btn-primary w-100"
          onClick={(e) => {
            e.stopPropagation();
            openPayment();
          }}
        >
          Přihlásit
        </button>
      );
    } else if (canRegister) {
      // Plno, ale přihlašování běží: zápis na čekací listinu je v přihlášce.
      action = (
        <button
          type="button"
          className="btn btn-outline-danger w-100"
          onClick={(e) => {
            e.stopPropagation();
            openPayment();
          }}
        >
          Plno – čekací listina
        </button>
      );
    } else if (isFull) {
      action = (
        <button type="button" className="btn btn-danger w-100" disabled>
          Plno
        </button>
      );
    } else {
      action = (
        <button type="button" className="btn btn-secondary w-100" disabled>
          Uzavřeno
        </button>
      );
    }
  } else {
    const paid = Number(reg.paidAmount ?? 0);
    const fee = Number(reg.startFee ?? 0);
    if (paid >= fee) {
      // „Zaplaceno" platí i pro závod zdarma (fee 0) – dřívější chyba
      // ukazovala u závodu zdarma „Přihlášen". Tlačítko zůstává klikací:
      // vede na detail registrace (odhlášení).
      action = (
        <button
          type="button"
          className="btn btn-outline-success w-100"
          onClick={(e) => {
            e.stopPropagation();
            openPayment();
          }}
        >
          <i className="bi bi-check-circle me-2" aria-hidden="true" />
          Zaplaceno
        </button>
      );
    } else {
      action = (
        <button
          type="button"
          className="btn btn-warning w-100"
          onClick={(e) => {
            e.stopPropagation();
            openPayment();
          }}
        >
          Přihlášen – čeká na platbu
        </button>
      );
    }
  }

  // Dialog kalendáře je portál, ale události z něj probublávají React stromem:
  // uvnitř klikací karty by každé klepnutí do dialogu otevřelo přihlášku –
  // proto je sourozencem karty.
  return (
    <>
    <div
      className="card mb-3"
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? openPayment : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              // Enter/mezerník na vnitřních tlačítkách (Propozice, Mapa…)
              // si obslouží tlačítka sama – karta reaguje jen na sebe.
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openPayment();
              }
            }
          : undefined
      }
      style={{
        cursor: isClickable ? "pointer" : "default",
        ...(race.organizedByLKS === false
          ? {
              background: "var(--lks-warning-bg)",
              borderColor: "var(--lks-warning-border)",
            }
          : null),
      }}
    >
      <div className="card-body">
        <div className="fw-bold mb-2" style={{ lineHeight: 1.3 }}>
          <DisciplineBadge
            race={race}
            className="me-2"
            style={{ verticalAlign: "middle" }}
          />
          {race.name}
          {weather && (
            <span
              className="ms-2 badge border border-secondary text-secondary bg-white"
              style={{ fontSize: "0.75rem" }}
            >
              <i className="bi bi-cloud-sun me-1" aria-hidden="true" />
              {weather}
            </span>
          )}
          {race.organizedByLKS === false && (
            <span
              className="ms-2 badge bg-warning text-dark"
              style={{ fontSize: "0.75rem" }}
            >
              Nepořádá LKS
            </span>
          )}
        </div>

        <p className="mb-1">
          <i className="bi bi-geo-alt me-2 text-secondary" />
          <strong>Střelnice:</strong> {race.shootingRange}
        </p>
        <p className="mb-1">
          <i className="bi bi-calendar-event me-2 text-secondary" />
          <strong>Datum:</strong> {raceDate}
        </p>

        {/* Uzávěrka se ukazuje VŽDY – i výchozí půlnoc dne závodu */}
        {registrationClosesAt && (
          <p className="mb-1">
            <i className="bi bi-clock me-2 text-secondary" />
            <strong>Registrace do:</strong> {formatDateTimeCZ(registrationClosesAt)}
          </p>
        )}

        <p className="mb-1">
          <i className="bi bi-cash-coin me-2 text-secondary" />
          <strong>Cena:</strong>{" "}
          {race.organizedByLKS === false
            ? "Závod nepořádá LKS"
            : price === 0
            ? "Zdarma"
            : `${price} Kč`}
        </p>

        {!reg && !canRegister && (
          <p className="mb-1 text-muted small">
            Přihlašování je uzavřeno (propozice zůstávají dostupné).
          </p>
        )}

        {race.maxCapacity != null && (
          <p className="mb-1">
            <i className="bi bi-people me-2 text-secondary" />
            <strong>Obsazenost:</strong> {regCount ?? "…"} / {race.maxCapacity}
            {isFull && <span className="ms-2 badge bg-danger">Plno</span>}
            {/* Po uzávěrce už listina nic neznamená (přihláška ji neukazuje) */}
            {isFull && onWaitlist && !reg && canRegister && (
              <span className="ms-2 badge bg-info">Na čekací listině</span>
            )}
          </p>
        )}

        {/* ── Banner: Jsem rozhodčí na tomto závodě ── */}
        {isJudge && (
          <div
            className="d-flex align-items-center p-2 rounded mt-2 mb-1"
            style={{ backgroundColor: "var(--lks-brand)", color: "#fff" }}
          >
            <i className="bi bi-broadcast fs-5 me-2" />
            <div className="flex-grow-1 me-2">
              <div className="fw-semibold small">
                Jste rozhodčí na tomto závodě
              </div>
              <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>
                Závod je aktivní
              </div>
            </div>
            <button
              type="button"
              className="btn btn-light btn-sm fw-semibold"
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(`/judge/${race.id}`);
              }}
            >
              Otevřít
            </button>
          </div>
        )}

        <div className="row g-2 mt-2 align-items-stretch">
          {linkItems.length > 0 && (
            <div className="col-12 col-md">
              {linkItems.map((item, index) => (
                <div
                  key={index}
                  className={index < linkItems.length - 1 ? "list-group mb-2" : "list-group"}
                >
                  {item}
                </div>
              ))}
            </div>
          )}

          <div className="col-12 col-md d-flex align-items-stretch">
            {action}
          </div>
        </div>
      </div>
    </div>
    {calendar.modal}
    </>
  );
};

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, userDoc } = useCurrentUser();
  // Přihláška na závod jen s ověřeným e-mailem (pravidla, bezpečnost 2) –
  // neověřený účet (relace ze starší verze aplikace) to uvidí hned tady.
  const emailCheck = useEmailVerification(user);

  const [juniorMaxAge, setJuniorMaxAge] = useState<number | null>(null);
  const [membershipFees, setMembershipFees] = useState<MembershipFee[]>([]);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [weatherData, setWeatherData] = useState<{ [raceId: string]: string }>(
    {}
  );
  const [shootingRanges, setShootingRanges] = useState<ShootingRange[]>([]);
  const [mapWarning, setMapWarning] = useState<{
    text: string;
    color: string;
  } | null>(null);
  const [raceRegistrationCounts, setRaceRegistrationCounts] = useState<{
    [raceId: string]: number;
  }>({});
  // Závody, na jejichž čekací listině uživatel je (raceId).
  const [waitlistRaceIds, setWaitlistRaceIds] = useState<Set<string>>(new Set());
  const uid = user ? user.uid : null;
  // Běžící závody, které uživatel skutečně rozhoduje (rozhodnutí majitele 6′,
  // stejně jako záložka „Rozhodčí“): hlavní rozhodčí nebo rozhodčí stanovišť
  // v races.liveJudgeUids. Jeden sdílený dotaz se spodní navigací, žádné
  // čtení stanovišť; znovu po návratu do aplikace (nejdřív po 30 s). Správce
  // bez role rozhodčího kartu nemá – bodování otevře ze závodu v Adminu
  // (Závody → „Rozhodčí“) nebo z nastavení živého bodování.
  const judgeRaces = useJudgeRaces(uid);
  const judgeRaceIds = useMemo(() => new Set(judgeRaces.map((r) => r.id)), [judgeRaces]);

  // Přihlášený uživatel odvozený ze sdíleného hooku (redirecty neřešíme,
  // nepřihlášené odchytí RequireAuth v App.tsx – jako dřív byl user null).
  const currentUser = useMemo<User | null>(() => {
    if (!user || !userDoc) return null;
    return {
      uid: user.uid,
      birthYear: userDoc.birthYear,
      gender: (userDoc.gender || "").toUpperCase(),
      membership: userDoc.membership || {},
      zpValidity: userDoc.zpValidity,
      zpNumber: userDoc.zpNumber,
      role: userDoc.role || "user",
      email: user.email || "",
      sequenceNumber: userDoc.sequenceNumber || "",
    };
  }, [user, userDoc]);

  /**
   * ✅ Závody:
   * - zobrazíme závody až do "závod + 3 dny" (kvůli propozicím)
   * - registrace bude povolená do registrationClosesAt:
   *   - pokud je v DB: použijeme ji
   *   - jinak default: začátek dne závodu (00:00 v Praze – jako pravidla)
   */
  const racesAsync = useAsync<Race[]>(async () => {
    // Viditelnost konci 3 dny po zavodu (viz vyse), takze starsi zavody
    // nema smysl vubec nacitat. Pole date je string "YYYY-MM-DD",
    // porovnani je proto lexikograficke. Den navic jako rezerva.
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 4);
    const cutoffStr = `${cutoff.getFullYear()}-${String(
      cutoff.getMonth() + 1
    ).padStart(2, "0")}-${String(cutoff.getDate()).padStart(2, "0")}`;

    let snapshot;
    try {
      snapshot = await getDocs(
        query(collection(db, "races"), where("date", ">=", cutoffStr))
      );
    } catch {
      // pojistka, kdyby dotaz neproSel (napr. chybejici index)
      snapshot = await getDocs(collection(db, "races"));
    }
    const data: Race[] = [];
    const now = new Date();

    snapshot.forEach((docSnap) => {
      const raceData = docSnap.data();

      // Testovací závod (generálka) jen pro správce a rozhodčí – členům ne
      // (rozhodčí ho mají v záložce Rozhodčí, utils/testRace.ts)
      if (isTestRace(raceData)) return;

      const raceDateObj = toDateSafe(raceData.date);
      if (!raceDateObj) return;

      const raceDayStart = startOfDay(raceDateObj);

      // ✅ default: propozice držíme ještě 3 dny po závodě (do konce dne)
      const visibleUntilDefault = endOfDay(
        new Date(raceDayStart.getTime() + 3 * 24 * 60 * 60 * 1000)
      );

      // ✅ uzávěrka z DB, jinak začátek dne závodu v Praze – stejně jako
      // přihláška a pravidla (utils/registrationDeadline); platné datum ji
      // vždy dá, raceDayStart je jen pojistka typu
      const registrationClosesAtFinal =
        registrationClosesAtOf(raceData) ?? raceDayStart;

      // ✅ filtr viditelnosti: závod ukazuj, dokud je "teď" <= visibleUntil
      if (now > visibleUntilDefault) {
        return;
      }

      data.push({
        id: docSnap.id,
        name: raceData.name,
        discipline: toDisciplineId(raceData.discipline),
        shootingRange: raceData.shootingRange,
        year: raceData.year,
        raceNumber: raceData.raceNumber,
        priceMember: raceData.priceMember,
        priceNonMember: raceData.priceNonMember,
        priceWoman: raceData.priceWoman,
        priceJunior: raceData.priceJunior,
        date: raceData.date,
        mapUrl: raceData.mapUrl,
        organizedByLKS: raceData.organizedByLKS,
        rulesPdfUrl: raceData.rulesPdfUrl || "",

        registrationClosesAt: registrationClosesAtFinal,
        visibleUntil: visibleUntilDefault,
        liveScoring: raceData.liveScoring || false,
        status: raceData.status || "",
        livePublic: raceData.livePublic !== false, // default true
        finishedAt: toDateSafe(raceData.finishedAt) ?? undefined,
        // 0 a prázdné = neomezeno (nápověda formuláře závodu; dřív 0 = „plno")
        maxCapacity: capacityOf(raceData) ?? undefined,
      });
    });

    data.sort((a, b) => {
      const dateA = toDateSafe(a.date)?.getTime() ?? 0;
      const dateB = toDateSafe(b.date)?.getTime() ?? 0;
      return dateA - dateB;
    });

    return data;
  }, []);

  const races = useMemo(() => racesAsync.data || [], [racesAsync.data]);

  // Obsazenost závodů s kapacitou z veřejného race_stats (cizí přihlášky už
  // číst nejde) – mimo useAsync, ať neblokuje první vykreslení seznamu
  // (selhání jen zaloguje a nechá „…" – jako dřív).
  useEffect(() => {
    const ids = races.filter((r) => r.maxCapacity != null).map((r) => r.id);
    if (ids.length === 0) return;
    let cancelled = false;
    loadRegisteredCounts(ids).then(
      (counts) => {
        if (!cancelled) setRaceRegistrationCounts(counts);
      },
      (err) => reportWarning("UserDashboard.loadRegisteredCounts", err)
    );
    return () => {
      cancelled = true;
    };
  }, [races]);

  // Vlastní čekací listiny (upozornění „Uvolnilo se místo" v aplikaci; e-mail
  // posílá server). Chyba čtení dashboard neshodí – jen bez upozornění a štítků.
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    loadMyWaitlist(uid).then(
      (entries) => {
        if (!cancelled) setWaitlistRaceIds(new Set(entries.map((e) => e.raceId)));
      },
      (err) => reportWarning("UserDashboard.loadMyWaitlist", err, { uid })
    );
    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    const fetchShootingRanges = async () => {
      const snap = await getDocs(collection(db, "shootingRanges"));
      setShootingRanges(
        snap.docs.map((doc) => {
          const data = doc.data();
          return {
            name: data.name,
            lat: data.lat,
            lng: data.lng,
          } as ShootingRange;
        })
      );
    };
    fetchShootingRanges();
  }, []);

  // Načteme aktivní záznamy z kolekce "membershipFees" pro roky >= aktuální rok
  useEffect(() => {
    const loadMembershipFees = async () => {
      try {
        const snapshot = await getDocs(collection(db, "membershipFees"));
        const feeArray: MembershipFee[] = [];
        const currentYear = new Date().getFullYear();
        snapshot.forEach((docSnap) => {
          const feeData = docSnap.data();
          if (feeData.active && feeData.year >= currentYear) {
            feeArray.push({
              id: docSnap.id,
              year: feeData.year,
              price: feeData.price,
              active: feeData.active,
            });
          }
        });
        feeArray.sort((a, b) => a.year - b.year);
        setMembershipFees(feeArray);
      } catch (error) {
        console.error("Chyba při načítání členství:", error);
      }
    };
    loadMembershipFees();
  }, []);

  // Načteme registrace aktuálního uživatele z kolekce "registrations"
  useEffect(() => {
    if (!currentUser) return;
    const loadRegistrations = async () => {
      try {
        const q = query(
          collection(db, "registrations"),
          where("userId", "==", currentUser.uid)
        );
        const querySnapshot = await getDocs(q);
        const regs: Registration[] = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          regs.push({
            id: docSnap.id,
            raceId: data.raceId,
            startFee: data.startFee,
            paidAmount: data.paidAmount,
          });
        });
        setRegistrations(regs);
      } catch (error) {
        console.error("Chyba při načítání registrací:", error);
      }
    };
    loadRegistrations();
  }, [currentUser]);

  useEffect(() => {
    const fetchJuniorAge = async () => {
      try {
        const snap = await getDoc(doc(db, "defaults", "categories"));
        if (snap.exists()) {
          const data = snap.data();
          const juniorAge = data?.junior?.maxAge;
          if (typeof juniorAge === "number") {
            setJuniorMaxAge(juniorAge);
          }
        }
      } catch (err) {
        console.error("Chyba při načítání věku juniorů:", err);
      }
    };
    fetchJuniorAge();
  }, []);

  useEffect(() => {
    const fetchMapWarning = async () => {
      try {
        const snap = await getDoc(doc(db, "defaults", "mapWarning"));
        if (snap.exists()) {
          const data = snap.data();
          if (data?.text) {
            setMapWarning({
              text: data.text,
              color: data.color || "danger",
            });
          }
        }
      } catch (error) {
        console.error("Chyba při načítání varovného textu:", error);
      }
    };
    fetchMapWarning();
  }, []);

  // Pomocná funkce – najde registraci pro daný závod
  const getRegistrationForRace = (raceId: string): Registration | undefined => {
    return registrations.find((reg) => reg.raceId === raceId);
  };

  useEffect(() => {
    const fetchWeather = async () => {
      // Klíč výhradně z prostředí (v Netlify je REACT_APP_OPENWEATHER_API_KEY);
      // bez klíče se počasí tiše vynechá – do zdrojáku klíč nepatří.
      const apiKey =
        process.env.REACT_APP_OPENWEATHER_API_KEY ||
        process.env.REACT_APP_OPENWEATHER_KEY;
      if (!apiKey) return;
      const forecasts: { [raceId: string]: string } = {};

      // 1️⃣ Kontrola cache (localStorage může být zakázané – private mode)
      const cacheKey = "weatherCache";
      let cacheStr: string | null = null;
      try {
        cacheStr = localStorage.getItem(cacheKey);
      } catch {}
      if (cacheStr) {
        try {
          const cache = JSON.parse(cacheStr);
          if (
            cache.timestamp &&
            Date.now() - cache.timestamp < 2 * 60 * 60 * 1000
          ) {
            // Pokud cache není starší než 2 hodiny
            setWeatherData(cache.data);
            return;
          }
        } catch (e) {
          console.warn("Chyba při čtení cache počasí:", e);
        }
      }

      // 2️⃣ Načtení z API
      for (const race of races) {
        const range = shootingRanges.find(
          (r) =>
            race.shootingRange.toLowerCase().includes(r.name.toLowerCase()) &&
            r.lat &&
            r.lng
        );

        if (!range) continue;

        const raceDate = toDateSafe(race.date);
        if (!raceDate) continue;

        const raceDateStr = raceDate.toISOString().split("T")[0];

        const url = `https://api.openweathermap.org/data/2.5/forecast?lat=${range.lat}&lon=${range.lng}&appid=${apiKey}&units=metric&lang=cz`;

        try {
          const res = await fetch(url);
          if (!res.ok) {
            console.warn(`Chyba ${res.status} při načítání počasí.`);
            continue;
          }

          const data = await res.json();
          const forecast = data?.list?.find((f: any) =>
            f.dt_txt?.startsWith(raceDateStr)
          );

          if (!forecast) {
            continue; // pro den závodu žádná předpověď – přeskočí závod
          }

          if (
            forecast?.main?.temp != null &&
            forecast.weather?.[0]?.description
          ) {
            forecasts[race.id] = `${forecast.main.temp.toFixed(1)}°C, ${
              forecast.weather[0].description
            }`;
          }
        } catch (err) {
          console.warn(`Nepodařilo se načíst počasí pro ${race.name}:`, err);
        }
      }

      // 3️⃣ Uložení do localStorage
      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            timestamp: Date.now(),
            data: forecasts,
          })
        );
      } catch {}
      setWeatherData(forecasts);
    };

    if (races.length > 0 && shootingRanges.length > 0) {
      fetchWeather();
    }
  }, [races, shootingRanges]);

  // ── Info karty ──
  // Uvolněné místo: uživatel je na čekací listině, nepřihlášený a přihlásit
  // se dá (otevřená uzávěrka – po ní nic, listina už nic neznamená). Závod
  // s kapacitou: známá obsazenost je pod kapacitou. Závod bez kapacity
  // (správce ji zrušil) = volno – upozornění zmizí přihláškou nebo odebráním
  // z listiny, které přihláška (PaymentRace) nabízí i bez kapacity.
  // Místo dostane, kdo se přihlásí dřív – upozornění vidí celá listina.
  const now = new Date();
  const freedRaces = races.filter((r) => {
    if (!waitlistRaceIds.has(r.id) || getRegistrationForRace(r.id)) return false;
    if (!isRegistrationOpen(r, now)) return false;
    if (r.maxCapacity == null) return true;
    const count = raceRegistrationCounts[r.id];
    return count !== undefined && !isRaceFull(count, r.maxCapacity);
  });
  const liveRaces = races.filter(
    (r) => r.liveScoring && r.status === "active" && r.livePublic !== false
  );
  // Výsledky ukončeného závodu – viditelné 2 dny po skončení,
  // nebo dokud je hl. rozhodčí ručně nevypne (livePublic).
  const finishedRaces = races.filter((r) => {
    if (!r.liveScoring || r.status !== "finished" || r.livePublic === false)
      return false;
    if (!r.finishedAt) return false;
    const twoDaysMs = 2 * 24 * 60 * 60 * 1000;
    return new Date().getTime() - r.finishedAt.getTime() < twoDaysMs;
  });
  const hasInfoCards =
    !emailCheck.verified ||
    freedRaces.length > 0 ||
    judgeRaces.length > 0 ||
    liveRaces.length > 0 ||
    finishedRaces.length > 0 ||
    !!mapWarning?.text;

  return (
    <>
      {hasInfoCards && (
        <div className="mb-3">
          {/* Neověřený e-mail – bez něj se na závod nepřihlásí */}
          {!emailCheck.verified && (
            <EmailVerificationNotice state={emailCheck} variant="dashboard" />
          )}

          {/* Čekací listina – uvolněné místo (spěchá, proto nahoře) */}
          {freedRaces.map((race) => (
            <InfoCard
              key={`freed-${race.id}`}
              icon="bi-bell-fill"
              tone="success"
              title="Uvolnilo se místo na závodě"
              subtitle={race.name}
              action={{
                label: "Přihlásit",
                variant: "primary",
                onClick: () => navigate(`/payment/${race.id}`),
              }}
            />
          ))}

          {/* Rozhodčí */}
          {judgeRaces.map((judgeRace) => (
            <InfoCard
              key={`judge-${judgeRace.id}`}
              icon="bi-broadcast-pin"
              tone="brand"
              title="Jste rozhodčí na aktivním závodě"
              subtitle={judgeRace.name}
              action={{
                label: "Otevřít",
                variant: "primary",
                onClick: () => navigate(`/judge/${judgeRace.id}`),
              }}
            />
          ))}

          {/* LIVE */}
          {liveRaces.map((race) => (
            <InfoCard
              key={`live-${race.id}`}
              icon="bi-broadcast"
              tone="success"
              title="Sledujte výsledky live"
              badge={<span className="badge bg-danger">LIVE</span>}
              subtitle={race.name}
              action={{
                label: "Otevřít",
                variant: "primary",
                onClick: () => navigate(`/display/${race.id}`),
              }}
            />
          ))}

          {/* Výsledky ukončeného závodu */}
          {finishedRaces.map((race) => (
            <InfoCard
              key={`fin-${race.id}`}
              icon="bi-flag-fill"
              tone="dark"
              title="Závod ukončen – výsledky jsou finální"
              subtitle={race.name}
              action={{
                label: "Výsledky",
                variant: "light",
                onClick: () => navigate(`/display/${race.id}`),
              }}
            />
          ))}

          {/* Zpráva adminů */}
          {mapWarning?.text &&
            (() => {
              const s = WARNING_TONES[mapWarning.color] || WARNING_TONES.warning;
              return (
                <InfoCard icon={s.icon} tone={s.tone} title={mapWarning.text} />
              );
            })()}
        </div>
      )}

      {/* Sekce Členské příspěvky – až po načtení uživatele (guard proti pádu) */}
      {currentUser && membershipFees.length > 0 && (
        <div className="mb-3">
          {membershipFees.map((fee) => {
            const status = currentUser.membership[fee.year.toString()];
            const paid = !!status && status.toUpperCase() === "ZAPLACENO";
            return paid ? (
              <InfoCard
                key={fee.id}
                icon="bi-check-lg"
                tone="success"
                title="Členství zaplaceno"
                subtitle={`Rok ${fee.year}`}
              />
            ) : (
              <InfoCard
                key={fee.id}
                icon="bi-exclamation-lg"
                tone="danger"
                title="Členství nezaplaceno"
                subtitle={`Rok ${fee.year} · ${fee.price} Kč`}
                action={{
                  label: "Zaplatit",
                  variant: "primary",
                  onClick: () => navigate(`/paymentmembership?year=${fee.year}`),
                }}
              />
            );
          })}
        </div>
      )}

      <hr className="my-separator" />

      {/* Sekce Závody */}
      <SectionHeader title="Závody" headingLevel={2} className="mb-2" />
      <AsyncState
        status={racesAsync.status}
        loadingText="Načítám závody…"
        emptyIcon="bi-calendar-x"
        emptyTitle="Zatím žádné závody"
        emptyText="Jakmile klub vypíše nový termín, objeví se tady."
        error={racesAsync.error}
        onRetry={racesAsync.reload}
      >
        {races.map((race) => (
          <RaceCard
            key={race.id}
            race={race}
            price={currentUser ? calculatePrice(race, currentUser, juniorMaxAge) : 0}
            reg={getRegistrationForRace(race.id)}
            regCount={raceRegistrationCounts[race.id] ?? null}
            onWaitlist={waitlistRaceIds.has(race.id)}
            weather={weatherData[race.id]}
            isJudge={judgeRaceIds.has(race.id)}
            onNavigate={navigate}
          />
        ))}
      </AsyncState>
    </>
  );
};

export default UserDashboard;
